import type { HandId, Landmarks, Vec2 } from '@store/handStore';
import type { CalibrationBucket } from './script';

export interface CapturedFrame {
  t: number;
  landmarks: Vec2[]; // plain 21-point copy
}

export interface CapturedBucket {
  id: string;
  hand: 'left' | 'right' | 'both';
  prompt: string;
  startedAt: number;
  endedAt: number;
  left: CapturedFrame[];
  right: CapturedFrame[];
}

function cloneLandmarks(lm: Landmarks): Vec2[] {
  const out: Vec2[] = new Array(lm.length);
  for (let i = 0; i < lm.length; i++) {
    const p = lm[i]!;
    out[i] = { x: p.x, y: p.y, z: p.z ?? 0 };
  }
  return out;
}

/**
 * Buffers labeled per-frame landmark captures. One instance per calibration
 * run. Frames are appended into the currently active bucket (or dropped if
 * no bucket is active — i.e. during rest gaps).
 */
export class CalibrationRecorder {
  private readonly buckets: CapturedBucket[] = [];
  private current: CapturedBucket | null = null;
  private lastSeenUpdatedAt: Record<HandId, number> = { left: 0, right: 0 };

  startBucket(bucket: CalibrationBucket): void {
    const now = performance.now();
    this.current = {
      id: bucket.id,
      hand: bucket.hand,
      prompt: bucket.prompt,
      startedAt: now,
      endedAt: 0,
      left: [],
      right: [],
    };
    this.lastSeenUpdatedAt = { left: 0, right: 0 };
  }

  /** Called every frame by the wizard's rAF loop. No-op if no active bucket. */
  observe(
    leftLm: Landmarks | null,
    leftUpdatedAt: number,
    rightLm: Landmarks | null,
    rightUpdatedAt: number,
  ): void {
    const b = this.current;
    if (!b) return;
    if (leftLm && leftUpdatedAt !== this.lastSeenUpdatedAt.left) {
      this.lastSeenUpdatedAt.left = leftUpdatedAt;
      b.left.push({ t: leftUpdatedAt, landmarks: cloneLandmarks(leftLm) });
    }
    if (rightLm && rightUpdatedAt !== this.lastSeenUpdatedAt.right) {
      this.lastSeenUpdatedAt.right = rightUpdatedAt;
      b.right.push({ t: rightUpdatedAt, landmarks: cloneLandmarks(rightLm) });
    }
  }

  endBucket(): void {
    if (!this.current) return;
    this.current.endedAt = performance.now();
    this.buckets.push(this.current);
    this.current = null;
  }

  getBuckets(): readonly CapturedBucket[] {
    return this.buckets;
  }

  abort(): void {
    this.current = null;
  }
}
